"use client";
import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { Chip } from './Chip.web.js';
import { defaultActive, toggleChip } from './chips.common.js';
import { Box } from '../Box/Box.web.js';
import { getGapDimensions } from '../Gap/utils.js';
import { useSmartControlled } from '../utils/hooks/useSmartControlled.js';
import { useTheme } from '../utils/theming/useTheme.js';
import { spacing } from '../constants/spacing.js';

var _excluded = ["items", "backgroundColor", "multiSelect", "active", "onChange", "activeIcon"];
/**
 * Chips help users filter content or select choices in an easy interactive way.
 */
var Chips = function Chips(props) {
  var items = props.items,
    backgroundColor = props.backgroundColor,
    _props$multiSelect = props.multiSelect,
    multiSelect = _props$multiSelect === undefined ? false : _props$multiSelect,
    activeProp = props.active,
    onChangeProp = props.onChange,
    activeIcon = props.activeIcon,
    rest = _objectWithoutProperties(props, _excluded);
  var _useSmartControlled = useSmartControlled(defaultActive, activeProp, onChangeProp),
    _useSmartControlled2 = _slicedToArray(_useSmartControlled, 2),
    active = _useSmartControlled2[0],
    setActive = _useSmartControlled2[1];
  var handleChange = React.useCallback(function (value) {
    return setActive(toggleChip(active, value, multiSelect));
  }, [active, setActive, multiSelect]);
  var theme = useTheme();
  var chipsTheme = theme.chips.web;

  // Refactored here to use Box (previously Gap) due to accessibility,
  // some screen readers require direct parent - child relationship when using aria props
  // and Gap adds additional intermediate
  var dimensions = getGapDimensions(spacing.xxxs);
  var wrapperHorizontalMargin = "calc(-".concat(dimensions.x, " / 2)");
  var wrapperVerticalMargin = "calc(-".concat(dimensions.y, " / 2)");
  var childHorizontalMargin = "calc(".concat(dimensions.x, " / 2)");
  var childVerticalMargin = "calc(".concat(dimensions.y, " / 2)");
  return /*#__PURE__*/React.createElement(Box, _extends({
    "data-testid": "elemental-Chips",
    display: "flex",
    flexWrap: "wrap",
    role: multiSelect ? 'group' : 'radiogroup',
    mh: wrapperHorizontalMargin,
    mv: wrapperVerticalMargin
  }, rest), items.map(function (item) {
    var _item$label;
    var isActive = active.includes(item.value);
    return /*#__PURE__*/React.createElement(Chip, {
      key: item.value,
      label: (_item$label = item.label) !== null && _item$label !== undefined ? _item$label : item.name,
      value: item.value,
      active: isActive,
      disabled: item.disabled,
      "aria-checked": isActive,
      role: multiSelect ? 'checkbox' : 'radio',
      color: backgroundColor ? backgroundColor : chipsTheme.color,
      onClick: handleChange,
      multiSelect: multiSelect,
      activeIcon: activeIcon,
      "data-testid": isActive ? "elemental-Chips-".concat(item.value, "-active") : "elemental-Chips-".concat(item.value),
      mh: childHorizontalMargin,
      mv: childVerticalMargin
    });
  }));
};

export { Chip, Chips };
